import moment from 'moment';
import { resolve_date_range_preset } from '../../../../components/DateRangePresets/dateRangePresets';

export const VOLUNTEERS_PERIOD_STORAGE_KEY = 'dashboard.volunteers.periodSelections';

export const DEFAULT_PERIOD_SELECTION = {
    value: 'preset:next30',
    custom_start: null,
    custom_end: null,
};

const DEFAULT_REQUEST_PRESET = 'next30';

export const CUSTOM_OPTION_VALUE = 'custom';

export const parseSelectionValue = (selectionValue) => {
    if (!selectionValue || typeof selectionValue !== 'string')
        return { kind: 'preset', value: 'next30' };
    if (selectionValue === CUSTOM_OPTION_VALUE)
        return { kind: 'custom', value: null };
    if (selectionValue.startsWith('phase:'))
        return { kind: 'phase', value: selectionValue.replace('phase:', '') || 'all' };
    if (selectionValue.startsWith('preset:'))
        return { kind: 'preset', value: selectionValue.replace('preset:', '') };
    return { kind: 'preset', value: 'next30' };
};

export const getSelectionForMission = (periodSelections, missionId) => {
    const saved = periodSelections?.[missionId];
    if (!saved || typeof saved !== 'object')
        return { ...DEFAULT_PERIOD_SELECTION };
    return {
        value: typeof saved.value === 'string' ? saved.value : DEFAULT_PERIOD_SELECTION.value,
        custom_start: saved.custom_start || null,
        custom_end: saved.custom_end || null,
    };
};

const resolveAllPhasesRange = (missionPhases) => {
    const phasesList = Array.isArray(missionPhases) ? missionPhases : [];
    if (!phasesList.length)
        return null;

    const starts = phasesList
        .map((phase) => moment(phase?.start_at).startOf('day'))
        .filter((start) => start.isValid());
    const ends = phasesList
        .map((phase) => moment(phase?.stop_at).endOf('day'))
        .filter((end) => end.isValid());

    if (!starts.length)
        return null;

    const start = starts.sort((a, b) => a.valueOf() - b.valueOf())[0];
    const end = ends.length
        ? ends.sort((a, b) => b.valueOf() - a.valueOf())[0]
        : null;
    return end ? { start, end } : { start, end: null };
};

export const resolveSelectionRange = (selection, missionPhases) => {
    const parsed = parseSelectionValue(selection?.value);
    if (parsed.kind === 'custom') {
        const start = selection?.custom_start ? moment(selection.custom_start).startOf('day') : null;
        const end = selection?.custom_end ? moment(selection.custom_end).endOf('day') : null;
        if (!start?.isValid() || !end?.isValid())
            return null;
        return { start, end };
    }
    if (parsed.kind === 'phase') {
        if (parsed.value === 'all')
            return resolveAllPhasesRange(missionPhases);
        const phase = (missionPhases || []).find((p) => String(p.id ?? p._id) === String(parsed.value));
        if (!phase)
            return null;
        const start = moment(phase.start_at).startOf('day');
        const end = moment(phase.stop_at).endOf('day');
        if (!start.isValid() || !end.isValid())
            return null;
        return { start, end };
    }
    return resolve_date_range_preset(parsed.value, moment());
};

const resolveDefaultRequestRange = () => {
    const range = resolve_date_range_preset(DEFAULT_REQUEST_PRESET, moment().startOf('day'));
    if (!range)
        return null;
    const start = range.start?.clone?.().startOf('day');
    const end = range.end?.clone?.().endOf('day') || null;
    if (!start?.isValid?.())
        return null;
    return { start, end };
};

const getItemEditionId = (item) => {
    if (!item)
        return null;
    const edition = item.edition;
    if (edition && typeof edition === 'object')
        return edition.id || edition._id || null;
    return edition || null;
};

const safeApplicationTypes = (applicationTypes) => Array.isArray(applicationTypes) ? applicationTypes : [];

export const readSavedPeriodSelections = () => {
    try {
        const raw = window.localStorage.getItem(VOLUNTEERS_PERIOD_STORAGE_KEY);
        const parsed = JSON.parse(raw || '{}');
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_error) {
        return {};
    }
};

export const buildRequestRange = ({
    isAdvancedMode,
    scopedApplicationTypes,
    periodSelections,
    phases,
}) => {
    const missionIds = !isAdvancedMode
        ? ['all']
        : scopedApplicationTypes
            .map((applicationType) => String(applicationType?.id ?? applicationType?._id))
            .filter(Boolean);
    const phasesForMission = (missionId) => {
        if (!isAdvancedMode || missionId === 'all')
            return phases;
        return (phases || []).filter(
            (phase) => String(phase?.application_type_id || '') === String(missionId)
        );
    };

    const resolvedRanges = missionIds
        .map((missionId) => resolveSelectionRange(
            getSelectionForMission(periodSelections, missionId),
            phasesForMission(missionId)
        ))
        .filter((range) => range?.start?.isValid?.());

    if (!resolvedRanges.length)
        return resolveDefaultRequestRange();

    const starts = resolvedRanges.map((range) => range.start.clone().startOf('day'));
    const ends = resolvedRanges
        .map((range) => range.end)
        .filter((end) => end?.isValid?.())
        .map((end) => end.clone().endOf('day'));

    const start = starts.sort((a, b) => a.valueOf() - b.valueOf())[0];
    const end = ends.length ? ends.sort((a, b) => b.valueOf() - a.valueOf())[0] : null;
    return start ? { start, end } : resolveDefaultRequestRange();
};

export const scopeApplicationTypes = (applicationTypes, hasEditions, currentEditionId) => (
    safeApplicationTypes(applicationTypes).filter((applicationType) => {
        if (!hasEditions || !currentEditionId)
            return true;
        return String(getItemEditionId(applicationType) || '') === String(currentEditionId);
    })
);

/** Stable cache intent: excludes computed date range (derived after phases load). */
export const buildDashboardVolunteersCacheKey = ({
    userId,
    dashboardScopeKey,
    currentEditionId,
    periodSelections,
    canUseTasks,
    canUseApplications,
    canUseShifts,
}) => [
    String(userId || ''),
    String(dashboardScopeKey || 'full'),
    String(currentEditionId || ''),
    JSON.stringify(periodSelections || {}),
    canUseTasks ? '1' : '0',
    canUseApplications ? '1' : '0',
    canUseShifts ? '1' : '0',
].join('::');

export const buildRequestFilters = (requestRange) => ({
    ...(requestRange?.start ? { start_at_gte: requestRange.start.toISOString() } : {}),
    ...(requestRange?.end ? { start_at_lte: requestRange.end.toISOString() } : {}),
});

export const buildPresetOptionValue = (presetId) => `preset:${presetId}`;
export const buildPhaseOptionValue = (phaseId) => `phase:${phaseId}`;
export const ALL_PHASES_OPTION_VALUE = 'phase:all';
